import axios from 'axios';
import srtParser2 from 'srt-parser-2';
import {
  type TranscriptionData,
  type TranscriptionWord,
  type TranscriptionSegment,
  mapWordsToSegments,
} from '@contentlab/api/project/types';

export interface SrtItem {
  id: string;
  startTime: string;
  startSeconds: number;
  endTime: string;
  endSeconds: number;
  currentText: string;
  originalText: string;
  wordTimings?: Word[];
}

interface Word {
  text: string;
  start: number;
  end: number;
}

export function srtArrayToBase64(srtArray: SrtItem[]) {
  if (srtArray.length === 0) {
    return null;
  }
  // eslint-disable-next-line new-cap
  const srtParser = new srtParser2();
  const parsedSrt = srtParser.toSrt(
    srtArray.map((item) => ({
      ...item,
      text: item.currentText,
    }))
  );
  const encoder = new TextEncoder();
  const uint8Array = encoder.encode(parsedSrt);
  const base64Srt = btoa(String.fromCharCode(...uint8Array));
  return base64Srt;
}

export function srtStringToSrtItems(srtString: string): SrtItem[] {
  // eslint-disable-next-line new-cap
  const srtParser = new srtParser2();
  const parsedSrt = srtParser.fromSrt(srtString);
  return parsedSrt.map((item) => ({
    ...item,
    currentText: item.text,
    originalText: item.text,
  }));
}

/**
 * Convert SRT string to TranscriptionData with uniform word timing distribution
 * Matches backend TranscriptionMapper.from_srt_with_uninformed_word_timing method
 */
export function srtStringToTranscriptionData(
  srtContent: string,
  language = 'en'
): TranscriptionData {
  // eslint-disable-next-line new-cap
  const srtParser = new srtParser2();
  const srtObjects = srtParser.fromSrt(srtContent);

  const words: TranscriptionWord[] = [];
  const segments: TranscriptionSegment[] = [];

  srtObjects.forEach((srtObj: any) => {
    const segmentWords = srtObj.text
      .split(' ')
      .filter((word: string) => word.trim());
    const segmentStart = srtObj.startSeconds; // Already in seconds
    const segmentEnd = srtObj.endSeconds; // Already in seconds
    const segmentDuration = segmentEnd - segmentStart;
    const wordDuration =
      segmentWords.length > 0 ? segmentDuration / segmentWords.length : 0;

    // Create word-level entries with uniform distribution
    segmentWords.forEach((word: string, index: number) => {
      const wordStart = segmentStart + index * wordDuration;
      // Ensure the last word ends exactly at segment end to avoid precision issues
      const wordEnd =
        index === segmentWords.length - 1
          ? segmentEnd
          : wordStart + wordDuration;

      words.push({
        word: word.trim(),
        start: wordStart,
        end: wordEnd,
      });
    });

    // Keep segment-level for compatibility
    segments.push({
      start: segmentStart,
      end: segmentEnd,
      text: srtObj.text,
    });
  });

  return {
    language,
    segments,
    words: words.length > 0 ? words : null,
  };
}

/**
 * Type guard to check if data is in the new transcription format
 */
export function isTranscriptionData(data: any): data is TranscriptionData {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.language === 'string' &&
    Array.isArray(data.segments) &&
    (data.words === null || Array.isArray(data.words))
  );
}

/**
 * Convert seconds to SRT time format (HH:MM:SS,mmm)
 * Matches backend format_timestamp function
 */
export function secondsToSrtTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const milliseconds = Math.floor((seconds % 1) * 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds
    .toString()
    .padStart(3, '0')}`;
}

/**
 * Convert TranscriptionData to SRT string format
 * Matches backend TranscriptionMapper.to_srt method
 */
export function transcriptionDataToSrt(
  transcriptionData: TranscriptionData
): string {
  const srtContent: string[] = [];

  transcriptionData.segments.forEach((segment, index) => {
    const sequenceNumber = index + 1;
    const startTime = secondsToSrtTime(segment.start);
    const endTime = secondsToSrtTime(segment.end);
    const text = segment.text.trim();

    srtContent.push(
      `${sequenceNumber}\n${startTime} --> ${endTime}\n${text}\n`
    );
  });

  return srtContent.join('\n');
}

/**
 * Convert transcription segments to SrtItem array
 */

export function transcriptionSegmentsToSrtItems(
  transcriptionData: TranscriptionData
): SrtItem[] {
  if (!transcriptionData.segments) {
    return [];
  }

  const wordsInSegments = mapWordsToSegments(transcriptionData);
  return transcriptionData.segments.map((segment, index) => ({
    id: (index + 1).toString(),
    startTime: secondsToSrtTime(segment.start),
    startSeconds: segment.start,
    endTime: secondsToSrtTime(segment.end),
    endSeconds: segment.end,
    currentText: segment.text,
    originalText: segment.text,
    wordTimings: wordsInSegments?.[index]?.map((word) => ({
      text: word.word,
      start: word.start,
      end: word.end,
    })),
  }));
}

/**
 * Download and parse transcription data - URL can return either SRT text or JSON with segments
 */
export async function downloadAndParseSrt(url: string): Promise<SrtItem[]> {
  const response = await axios.get(url);

  // Check if the response is JSON with transcription segments
  if (isTranscriptionData(response.data)) {
    return transcriptionSegmentsToSrtItems(response.data);
  }

  // Fallback to existing SRT parsing for backward compatibility
  return srtStringToSrtItems(response.data);
}

export function hasSrtItemBeenEdited(
  item: SrtItem,
  originalItem: SrtItem
): boolean {
  return item.currentText !== originalItem.currentText;
}

export function hasTranscriptionBeenEdited(
  currentSrt: SrtItem[],
  originalSrt: SrtItem[]
): boolean {
  return currentSrt.some((item, index) => {
    const originalItem = originalSrt[index];
    return hasSrtItemBeenEdited(item, originalItem);
  });
}

export function srtItemsToTranscriptionData(
  srtItems: SrtItem[],
  language: string
): TranscriptionData {
  const segments: TranscriptionSegment[] = srtItems.map((item) => ({
    start: item.startSeconds,
    end: item.endSeconds,
    text: item.currentText,
  }));

  const words: TranscriptionWord[] = [];
  srtItems.forEach((item) => {
    if (item.wordTimings) {
      item.wordTimings.forEach((word) => {
        words.push({
          word: word.text,
          start: word.start,
          end: word.end,
        });
      });
    }
  });

  return {
    language,
    segments,
    words: words.length > 0 ? words : null,
  };
}
